import { useRef, useState } from "react";
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Link,
  Chip,
  Divider,
  Button,
} from "@mui/material";
import {
  ChevronDown,
  ChevronUp,
  X,
  MemoryStick,
  ListTodo,
  EllipsisVertical,
} from "lucide-react";
import useIsMobile from "../../components/hooks/useIsMobile";
import DateDisplay from "../../components/common/DateDisplay";
import { AdvisorApplication } from "../../protogen/advisor_pipeline_service_pb";
import MetadataSection from "../../components/common/MetadataSection";
import {
  useUpdateAdvisorApplication,
  useSendAdvisorMeetingInvitation,
} from "../../services/advisorPipeline";
import { formatUSPhoneNumber } from "../../common/utils";
import {
  BasicDetail,
  ContactDetail,
  MultiSelectDetail,
  TrackingDetails,
} from "./Details";
import {
  AdvisorApplicationStatus,
  NEXT_PIPELINE_STATUS_MAP,
} from "./advisorFlow";
import PopperMenu, { createMenuOption } from "components/common/PopperMenu";
import AdvisorApplicationNotes from "./AdvisorApplicationNotes";
import LinkRouter from "components/navigation/LinkRouter";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import WorkProfile from "./WorkProfile";

const leftFlex = 3;
const rightFlex = 5;
// Mapping of status name to Title, potential subtext, and color
const STATUS_MAP: Record<
  AdvisorApplicationStatus,
  {
    label: string;
    bgColor: string;
    textColor: string;
    subtext?: string;
    subtextColor?: string;
    subtextBgColor?: string;
  }
> = {
  // New
  in_review: {
    label: "New application",
    bgColor: "#FFF3D6", // Yellow tint
    textColor: "#6B6E7B",
  },
  // In process
  qualified_app: {
    label: "In process",
    subtext: "Qualified App",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  waitlist: {
    label: "In process",
    subtext: "Qualified - Waitlist",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  interview: {
    label: "In process",
    subtext: "Send interview invitation",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  no_show: {
    label: "In process",
    subtext: "No Show Interview",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  meeting: {
    label: "In process",
    subtext: "Meeting",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  ready_ttt: {
    label: "In process",
    subtext: "Ready for TTT",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  ttt_sent: {
    label: "In process",
    subtext: "TTT Sent",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  send_offer: {
    label: "In process",
    subtext: "Send Offer",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  offer_sent: {
    label: "In process",
    subtext: "FA Offer Sent",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  accepted: {
    label: "In process",
    subtext: "Offer Accepted",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  onboarding: {
    label: "In process",
    subtext: "Onboarding Scheduled",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  active: {
    label: "Active",
    subtext: "Currently working",
    bgColor: "#E6F4EA", // Green tint
    textColor: "#1E8E3E",
  },
  // All
  on_hold: {
    label: "On Hold",
    subtext: "Temporarily paused",
    bgColor: "#FFE0E0", // Red tint
    textColor: "#D93025",
  },
  rejected: {
    label: "Rejected",
    subtext: "Not moving forward",
    bgColor: "#FFE0E0",
    textColor: "#D93025",
  },
  churned: {
    label: "Churned",
    subtext: "No longer active",
    bgColor: "#FFE0E0",
    textColor: "#D93025",
  },
  never_active: {
    label: "Churned - Never Active",
    subtext: "Did not start",
    bgColor: "#FFE0E0",
    textColor: "#D93025",
  },
};

const StatusAction = ({
  label,
  variant,
  disabled,
  onClick,
  status,
  applicationRef,
}: {
  label: string;
  disabled: boolean;
  variant?: "primary" | "warning" | "error";
  onClick: () => void;
  status: AdvisorApplicationStatus;
  applicationRef: string;
}) => {
  const { pushSnack } = useSnackpack();
  const { request: sendInvitationRequest, loading: invitationLoading } =
    useSendAdvisorMeetingInvitation();
  const handleSendInvitation = async () => {
    if (invitationLoading) return;
    try {
      await sendInvitationRequest({
        applicationRef: applicationRef,
      });
      pushSnack("Applicant has been emailed to schedule a meeting!");
    } catch (error) {
      console.error("Failed to send invitation:", error);
    }
  };
  const onChange = async () => {
    // Send invitation email when moving to qualified_app status
    if (status === "interview") {
      await handleSendInvitation();
    }
    onClick();
  };

  // Define styles based on variant
  const getButtonStyles = () => {
    switch (variant) {
      case "primary":
        return {
          variant: "contained" as const,
          sx: {
            backgroundColor: "#2A5A8A", // Dark blue background
            color: "#FFFFFF", // White text
            border: "1px solid #2A5A8A",
            borderRadius: "20px",
            textTransform: "none" as const,
            fontWeight: 500,
            fontSize: "14px",
            padding: "6px 16px",
            "&:hover": {
              backgroundColor: "#1E4A7A", // Darker blue on hover
              border: "1px solid #1E4A7A",
            },
            "&:disabled": {
              backgroundColor: "#B0B0B0",
              color: "#FFFFFF",
              border: "1px solid #B0B0B0",
            },
          },
        };
      case "error":
        return {
          variant: "outlined" as const,
          sx: {
            backgroundColor: "transparent",
            color: "#D93025", // Red text
            border: "1px solid #D93025", // Red border
            borderRadius: "20px",
            textTransform: "none" as const,
            fontWeight: 500,
            fontSize: "14px",
            padding: "6px 16px",
            "&:hover": {
              backgroundColor: "#FFF5F5", // Light red background on hover
              border: "1px solid #D93025",
            },
            "&:disabled": {
              color: "#B0B0B0",
              border: "1px solid #B0B0B0",
            },
          },
        };
      default:
        return {
          variant: "outlined" as const,
          sx: {
            backgroundColor: "transparent",
            color: "#5B9BD5", // Blue text
            border: "1px solid #5B9BD5", // Blue border
            borderRadius: "20px",
            textTransform: "none" as const,
            fontWeight: 500,
            fontSize: "14px",
            padding: "6px 16px",
            "&:hover": {
              backgroundColor: "#F0F8FF", // Light blue background on hover
              border: "1px solid #5B9BD5",
            },
            "&:disabled": {
              color: "#B0B0B0",
              border: "1px solid #B0B0B0",
            },
          },
        };
    }
  };

  const buttonStyles = getButtonStyles();

  return (
    <Button
      variant={buttonStyles.variant}
      size="small"
      onClick={onChange}
      disabled={disabled || invitationLoading}
      sx={buttonStyles.sx}
    >
      {label}
    </Button>
  );
};

const StatusActions = ({
  currentStatus,
  disabled,
  onClick,
  applicationRef,
}: {
  currentStatus: AdvisorApplicationStatus;
  disabled: boolean;
  onClick: (status: AdvisorApplicationStatus) => Promise<void>;
  applicationRef: string;
}) => {
  const nextStatuses = NEXT_PIPELINE_STATUS_MAP[currentStatus];
  return (
    <>
      {nextStatuses.map((status) => (
        <StatusAction
          key={status.name}
          status={status.name}
          applicationRef={applicationRef}
          label={
            STATUS_MAP[status.name].subtext || STATUS_MAP[status.name].label
          }
          variant={status.variant}
          disabled={disabled}
          onClick={() => onClick(status.name)}
        />
      ))}
    </>
  );
};

const StatusChip = ({
  label,
  bgColor,
  textColor,
  subtext,
  subtextColor,
  subtextBgColor,
}: {
  label: string;
  bgColor: string;
  textColor: string;
  subtext?: string;
  subtextColor?: string;
  subtextBgColor?: string;
}) => {
  const isMobile = useIsMobile();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: "start",
        gap: "2px",
      }}
    >
      <Chip
        label={label}
        sx={{
          backgroundColor: bgColor,
          color: textColor,
          fontWeight: 600,
          fontSize: "12px",
          height: "24px",
        }}
      />
      {subtext && (
        <Chip
          label={subtext}
          sx={{
            backgroundColor: subtextBgColor || "#F5F5F5",
            color: subtextColor || "#6B6E7B",
            fontWeight: 400,
            fontSize: "11px",
            height: "20px",
          }}
        />
      )}
    </Box>
  );
};

const getStatus = (application: AdvisorApplication) => {
  const status = application.status.toLowerCase();
  const statusConfig = STATUS_MAP[status as keyof typeof STATUS_MAP] || {
    label: status,
    bgColor: "#FFF",
    textColor: "#6B6E7B",
  };

  return <StatusChip {...statusConfig} />;
};

const ApplicationDetails = ({
  application,
}: {
  application: AdvisorApplication;
}) => {
  const isMobile = useIsMobile();
  return (
    <Box sx={{ padding: "0", flex: leftFlex }}>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        gap="10px"
      >
        {isMobile && (
          <Typography
            variant="body"
            sx={{ display: "flex", alignItems: "center", gap: "5px" }}
          >
            <DateDisplay
              date={new Date(Number(application.lastActivitySec) * 1000)}
            />
          </Typography>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Contact info */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <Typography variant="bodyHeavy">Contact info</Typography>
            <ContactDetail
              icon="Phone"
              content={
                <Link
                  href={`/inbox/phone/create?phone=${encodeURIComponent(
                    application.phoneNumber || "",
                  )}`}
                  sx={{ fontWeight: 500 }}
                >
                  {formatUSPhoneNumber(application.phoneNumber)}
                </Link>
              }
            />
            <ContactDetail
              icon="Mail"
              content={
                <Link
                  href={`/inbox/email/create?email=${encodeURIComponent(
                    application.email || "",
                  )}`}
                  sx={{ fontWeight: 500 }}
                >
                  {application.email}
                </Link>
              }
            />
            <ContactDetail
              icon="MapPin"
              content={
                <Link
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    application.location,
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontWeight: 500 }}
                >
                  {application.location}
                </Link>
              }
            />
            <ContactDetail
              icon="Linkedin"
              content={
                <Link
                  href={application.linkedinProfile}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontWeight: 500 }}
                >
                  LinkedIn
                </Link>
              }
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ApplicationMetadata = ({
  application,
}: {
  application: AdvisorApplication;
}) => {
  const { request } = useUpdateAdvisorApplication();

  if (application.metadata === undefined) return null;
  return (
    <MetadataSection
      metadata={application.metadata}
      saveMetadata={async (metadata) => {
        await request({
          ref: application.ref,
          updatedStatus: "",
          shouldUpdateMetadata: true,
          updatedMetadata: metadata,
        });
      }}
      entityType={"family"}
    />
  );
};

const ApplicationContent = ({
  application,
}: {
  application: AdvisorApplication;
}) => {
  const isMobile = useIsMobile();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: "24px",
        borderTop: "1px solid #E2E3E4",
        padding: isMobile ? "20px 0 0" : "20px 0",
      }}
    >
      {/* Left */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: leftFlex,
          gap: "20px",
        }}
      >
        <BasicDetail
          label="Cities with expertise"
          content={application.citiesWithExpertise}
        />
        <MultiSelectDetail
          label={"Superpowers"}
          Icon={MemoryStick}
          items={application.superpowers}
        />
        <BasicDetail
          label="Organization strategy"
          content={application.organizationStrategy}
        />
        <BasicDetail
          label="Has flexibility"
          content={application.hasFlexibility ? "Yes" : "No"}
        />
        <BasicDetail
          label="Willing to run errands"
          content={application.willingToRunErrands ? "Yes" : "No"}
        />
        <MultiSelectDetail
          label="Favorite tasks"
          Icon={ListTodo}
          items={application.favoriteTasks}
        />
        <BasicDetail
          label="Growth strategy"
          content={application.businessPlan}
        />
        <BasicDetail label="Advisor Interest" content={application.whyFa} />
        <BasicDetail
          label="Sample plan"
          content={application.friendVisitPlan}
        />
        <BasicDetail label="Referral" content={application.referralSource} />
        <TrackingDetails trackingParameters={application.trackingParams} />
      </Box>
    </Box>
  );
};

interface AdvisorApplicationCardProps {
  application: AdvisorApplication;
  forceOpen?: boolean;
  onClose: () => void;
  onOpen: () => void;
  refresh: () => Promise<void>;
  hideActions?: boolean;
}

export default ({
  application,
  forceOpen = false,
  onClose,
  onOpen,
  refresh,
  hideActions = false,
}: AdvisorApplicationCardProps) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(forceOpen);
  const { request: updateRequest, loading } = useUpdateAdvisorApplication();
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const handleStatusMenuToggle = () => {
    setStatusMenuOpen((prevOpen) => !prevOpen);
  };
  const statusMenuAnchorRef = useRef<HTMLButtonElement>(null);

  const handleUpdate = async (status: string) => {
    if (loading) return;
    await updateRequest({
      ref: application.ref,
      updatedStatus: status,
      shouldUpdateMetadata: false,
      updatedMetadata: undefined,
    });
    setStatusMenuOpen(false);
    onClose();
    await refresh();
  };

  const handleOpen = () => {
    setOpen(true);
    onOpen();
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        borderRadius: "8px",
        border: "1px solid #ECECEC",
        width: "100%",
        background: open ? "#FBFBFB" : "white",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "start" : "center",
          gap: "12px",
          cursor: !open ? "pointer" : undefined,
          padding: isMobile ? "12px 20px" : "16px 24px",
        }}
        onClick={() => !open && handleOpen()}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: isMobile ? "space-between" : "",
          }}
        >
          <Box
            sx={{
              flex: isMobile ? "" : leftFlex,
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              overflow: "hidden",
            }}
          >
            {application.advisorRef ? (
              <LinkRouter
                to={`/tools/advisor-administration/${encodeURIComponent(
                  application.advisorRef,
                )}`}
              >
                <Typography variant={isMobile ? "h4" : "bodyHeavy"}>
                  {`${application.firstName} ${application.lastName}`}
                </Typography>
              </LinkRouter>
            ) : (
              <Typography variant={isMobile ? "h4" : "bodyHeavy"}>
                {`${application.firstName} ${application.lastName}`}
              </Typography>
            )}
            <Typography variant="body" color="text.secondary">
              {application.location}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flex: isMobile ? "" : rightFlex,
              justifyContent: "space-between",
            }}
          >
            {!isMobile && getStatus(application)}
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {!isMobile && (
                <Typography variant="body">
                  <DateDisplay
                    date={new Date(Number(application.lastActivitySec) * 1000)}
                  />
                </Typography>
              )}
              {forceOpen ? (
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => handleClose()}
                >
                  <X />
                </IconButton>
              ) : (
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => setOpen(!open)}
                >
                  {open ? <ChevronUp /> : <ChevronDown />}
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>

        {isMobile && getStatus(application)}
      </Box>
      <Collapse in={open || forceOpen} timeout="auto" unmountOnExit>
        <Divider sx={{ borderColor: "#ECECEC" }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            background: "white",
            padding: "0px 24px 16px",
          }}
        >
          {/* Top half*/}
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: "24px",
              padding: "20px 0",
            }}
          >
            {/* Left side */}
            <ApplicationDetails application={application} />

            {/* Right side */}
            <Box
              sx={{
                flex: rightFlex,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: "16px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <ApplicationMetadata application={application} />
                {application.workProfile && (
                  <WorkProfile
                    workProfileJson={application.workProfile}
                    linkedinUrl={application.linkedinProfile}
                  />
                )}
              </Box>

              {/* Actions */}
              {!hideActions && (
                <Box
                  display="flex"
                  flexDirection={isMobile ? "row-reverse" : "row"}
                  gap={isMobile ? "24px" : "10px"}
                  justifyContent={isMobile ? "start" : "end"}
                  alignItems="center"
                >
                  <StatusActions
                    currentStatus={
                      application.status as AdvisorApplicationStatus
                    }
                    disabled={loading}
                    onClick={async (status) => {
                      await handleUpdate(status);
                    }}
                    applicationRef={application.ref}
                  />
                  <Box>
                    <IconButton
                      ref={statusMenuAnchorRef}
                      onClick={handleStatusMenuToggle}
                    >
                      <EllipsisVertical size={20} color="#8E9598" />
                    </IconButton>
                    <PopperMenu
                      open={statusMenuOpen}
                      anchorRef={statusMenuAnchorRef}
                      handleClose={() => {
                        setStatusMenuOpen(false);
                      }}
                    >
                      {Object.entries(STATUS_MAP)
                        .filter(([key]) => {
                          if (key === application.status) {
                            return false;
                          }
                          return true;
                        })
                        .map(([key, config]) =>
                          createMenuOption(
                            "subtext" in config && config.subtext
                              ? config.subtext
                              : config.label,
                            async () => {
                              await handleUpdate(key);
                            },
                            "",
                          ),
                        )}
                    </PopperMenu>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Bottom half */}
          <ApplicationContent application={application} />
          {open && <AdvisorApplicationNotes application={application} />}
        </Box>
      </Collapse>
    </Box>
  );
};
